import { useCallback, useMemo, useState } from "react"
import { type AutocompleteChangeReason } from "@octopus-energy/coral/atoms"
import { FormikAutocomplete } from "@octopus-energy/coral/formik"
import { useDebounceCallback } from "@oefr/hooks"
import { fetchAddresses } from "@oefr/services/data-gouv-addresses"
import { identity } from "@oefr/utils/react"
import { sleepInMs } from "@oefr/utils/sleepInMs"
import { useFormikContext } from "formik"
import { useTranslations } from "next-intl"
import { isNonNull } from "remeda"
import { formatDataGouvAddresses } from "@/utils/format-data-gouv-addresses"

type DataGouvAddress = Awaited<ReturnType<typeof formatDataGouvAddresses>>[number]

type Address = Omit<DataGouvAddress, "addrStreetLabel"> & {
    addrStreetLabel?: DataGouvAddress["addrStreetLabel"]
}

interface Props<TValue> {
    disabled?: boolean
    fieldName?: string
    fieldValueName?: keyof TValue
    label?: string
    onOptionSelect?: () => void
    defaultAddress?: Address
}

export function AddressField({
    disabled = false,
    fieldName = "address",
    fieldValueName = "fullAddress",
    label,
    onOptionSelect,
    defaultAddress,
}: Props<Address>) {
    const t = useTranslations("AddressField")
    const [isLoading, setIsLoading] = useState(false)
    const [hasEnoughSearchCharacter, setHasEnoughSearchCharacter] = useState(false)
    const [fieldOptions, setFieldOptions] = useState<Address[]>(
        defaultAddress ? [defaultAddress] : []
    )

    const { setFieldValue, validateField } = useFormikContext()

    const options = useMemo(
        () =>
            fieldOptions.map((o) => ({
                label: o[fieldValueName] ?? "",
                value: o[fieldValueName] ?? "",
            })),
        [fieldOptions, fieldValueName]
    )

    const handleInputChange = useCallback(async (value: string) => {
        if (value.trim().length < 3) {
            // French government API does not look up addresses for a less-than-3-character search
            setHasEnoughSearchCharacter(false)
            setFieldOptions([])

            return
        }

        setHasEnoughSearchCharacter(true)
        setIsLoading(true)

        try {
            const [options] = await Promise.all([fetchAddresses(value), sleepInMs(1000)])

            setFieldOptions(formatDataGouvAddresses(options))
        } finally {
            setIsLoading(false)
        }
    }, [])

    const debounced = useDebounceCallback(handleInputChange, 300)

    const handleChange = useCallback(
        async (_label: string | null, value: string | null, reason: AutocompleteChangeReason) => {
            if (disabled) return

            switch (reason) {
                case "selectOption": {
                    if (!fieldOptions.length) break
                    const matchingOption = fieldOptions.find((o) => o[fieldValueName] === value)

                    if (matchingOption) {
                        await setFieldValue(fieldName, matchingOption)
                        onOptionSelect?.()
                    } else {
                        await setFieldValue(
                            fieldName,
                            {
                                addrPostCode: "",
                                addrStreetLabel: "",
                                addrNumStreet: "",
                                addrCityLabel: "",
                            },
                            false
                        )
                    }

                    await validateField(fieldName)
                    break
                }

                case "clear":

                default: {
                    await setFieldValue(
                        fieldName,
                        {
                            fullAddress: "",
                            addrPostCode: "",
                            addrStreetLabel: "",
                            addrNumStreet: "",
                            addrCityLabel: "",
                        },
                        false
                    )

                    break
                }
            }
        },
        [
            disabled,
            fieldName,
            fieldOptions,
            fieldValueName,
            setFieldValue,
            validateField,
            onOptionSelect,
        ]
    )

    return (
        <FormikAutocomplete
            name={`${fieldName}.${String(fieldValueName)}`}
            label={label || t("completeAddress")}
            disabled={disabled}
            isLoading={isLoading}
            options={options}
            onChange={handleChange}
            onInputChange={(value) => isNonNull(value) && debounced(value)}
            noDataLabel={hasEnoughSearchCharacter ? t("noMatches") : t("minimumCharacters")}
            loadingText={t("loading")}
            filterOptions={identity}
        />
    )
}

AddressField.messages = ["AddressField"] as const
